import { useState, useCallback } from 'react';
import { gql, useMutation as useApolloMutation } from '@apollo/client';
import SparkMD5 from 'spark-md5';
import { enqueueSnackbar } from 'notistack';
import {
  CreateDirectUploadMutation,
  CreateDirectUploadMutationVariables,
} from '../graphql/__generated__/operations';

const CREATE_DIRECT_UPLOAD = gql`
  mutation CreateDirectUpload($input: CreateDirectUploadInput!) {
    createDirectUpload(input: $input) {
      directUploadResult {
        directUpload {
          url
          headers
        }
        signedId
      }
      errors {
        message
      }
    }
  }
`;

interface UseDirectUploadOptions {
  onSuccess?: (signedId: string) => void;
  onError?: (error: Error) => void;
}

interface UseDirectUploadReturn {
  uploadFile: (file: File) => Promise<string | null>;
  uploading: boolean;
}

/**
 * Calculate MD5 hash of a file and return it as a base64-encoded string
 * Note: Web Crypto API doesn't support MD5, so we use spark-md5 library
 */
export const calculateFileChecksum = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice;
    const chunkSize = 2097152; // Read in chunks of 2MB
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    const loadNext = () => {
      const start = currentChunk * chunkSize;
      const end =
        start + chunkSize >= file.size ? file.size : start + chunkSize;
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    };

    fileReader.onload = (e) => {
      if (e.target?.result) {
        spark.append(e.target.result as ArrayBuffer);
        currentChunk += 1;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          // Get hex hash and convert to base64
          const hexHash = spark.end();
          // Convert hex string to bytes, then to base64
          const bytes = new Uint8Array(
            hexHash.match(/.{1,2}/g)!.map((byte: string) => parseInt(byte, 16))
          );
          const binary = Array.from(bytes, (byte: number) =>
            String.fromCharCode(byte)
          ).join('');
          const base64Hash = btoa(binary);
          resolve(base64Hash);
        }
      }
    };

    fileReader.onerror = () => {
      reject(new Error('Failed to read file for checksum calculation'));
    };

    loadNext();
  });

/**
 * Upload file to pre-signed URL
 */
const uploadToPresignedUrl = async (
  file: File,
  url: string,
  headers: Record<string, string>
): Promise<void> => {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...headers,
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
};

/**
 * Hook for handling direct file uploads
 *
 * @param options - Configuration options including callbacks
 * @returns Object with uploadFile function and uploading state
 */
export const useDirectUpload = (
  options: UseDirectUploadOptions = {}
): UseDirectUploadReturn => {
  const [uploading, setUploading] = useState(false);
  const { onSuccess, onError } = options;

  const [createDirectUploadMutation] = useApolloMutation<
    CreateDirectUploadMutation,
    CreateDirectUploadMutationVariables
  >(CREATE_DIRECT_UPLOAD);

  const uploadFile = useCallback(
    async (file: File): Promise<string | null> => {
      setUploading(true);

      try {
        // Step 1: Analyze file and calculate checksum
        const checksum = await calculateFileChecksum(file);
        const fileMetadata = {
          byteSize: file.size,
          contentType: file.type || 'application/octet-stream',
          filename: file.name,
          checksum,
        };

        // Step 2: Get pre-signed URL from createDirectUpload mutation
        const result = await createDirectUploadMutation({
          variables: {
            input: fileMetadata,
          },
        });

        const mutationData = result.data;
        if (!mutationData?.createDirectUpload) {
          throw new Error('No response from createDirectUpload mutation');
        }

        const { createDirectUpload } = mutationData;

        if (createDirectUpload.errors && createDirectUpload.errors.length > 0) {
          const errorMessage = createDirectUpload.errors[0].message;
          throw new Error(errorMessage);
        }

        const { directUploadResult } = createDirectUpload;
        if (!directUploadResult) {
          throw new Error('No direct upload result returned');
        }

        const { directUpload, signedId } = directUploadResult;
        if (!directUpload || !signedId) {
          throw new Error('Invalid direct upload result');
        }

        // Step 3: Upload file to pre-signed URL
        await uploadToPresignedUrl(
          file,
          directUpload.url,
          directUpload.headers
        );

        // Step 4: Call success callback if provided
        if (onSuccess) {
          onSuccess(signedId);
        }

        setUploading(false);
        return signedId;
      } catch (error) {
        setUploading(false);
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';
        enqueueSnackbar(errorMessage, { variant: 'error' });

        if (onError) {
          onError(error instanceof Error ? error : new Error(errorMessage));
        }

        return null;
      }
    },
    [createDirectUploadMutation, onSuccess, onError]
  );

  return { uploadFile, uploading };
};
